import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { portalApi } from '@/services/portal-api';

/**
 * Inteligência de Mercado — Contratos do PNCP (IMC2/IMC3).
 *
 * Seleciona o cliente HTTP conforme o contexto (dado público, sem tenant):
 * - App principal / staff: `api` + `/mercado/contratos/...` (authenticate).
 * - Portal da empresa: `portalApi` + `/portal/mercado/contratos/...` (authenticateEmpresa).
 */
function mercadoClient(portal?: boolean) {
  if (portal) {
    return {
      get: <T,>(p: string) => portalApi.get<T>(`/portal/mercado/contratos${p}`),
      post: <T,>(p: string, body?: unknown) => portalApi.post<T>(`/portal/mercado/contratos${p}`, body),
      delete: <T,>(p: string) => portalApi.delete<T>(`/portal/mercado/contratos${p}`),
    };
  }
  return {
    get: <T,>(p: string) => api.get<T>(`/mercado/contratos${p}`),
    post: <T,>(p: string, body?: unknown) => api.post<T>(`/mercado/contratos${p}`, body),
    delete: <T,>(p: string) => api.delete<T>(`/mercado/contratos${p}`),
  };
}

interface Envelope<T> { success: boolean; data: T }

export interface ContratoRenovacao {
  numero_controle_pncp: string;
  numero_controle_pncp_compra: string | null;
  objeto_contrato: string | null;
  orgao_razao_social: string | null;
  orgao_cnpj: string | null;
  unidade_uf: string | null;
  unidade_municipio: string | null;
  fornecedor_nome: string | null;
  fornecedor_ni: string | null;
  valor_global: number | string | null;
  data_vigencia_inicio: string | null;
  data_vigencia_fim: string | null;
  dias_para_fim: number | null;
}

export interface RankingOrgao {
  orgao_cnpj: string;
  orgao_razao_social: string | null;
  unidade_uf: string | null;
  total_contratos: number;
  valor_total: number | string | null;
}

export interface RankingFornecedor {
  fornecedor_ni: string;
  fornecedor_nome: string | null;
  total_contratos: number;
  valor_total: number | string | null;
}

export interface GrandeContrato {
  numero_controle_pncp: string;
  objeto_contrato: string | null;
  orgao_razao_social: string | null;
  unidade_uf: string | null;
  unidade_municipio: string | null;
  fornecedor_nome: string | null;
  fornecedor_ni: string | null;
  valor_global: number | string | null;
  data_assinatura: string | null;
  data_vigencia_fim: string | null;
}

export interface ResumoContratos {
  total_contratos: number;
  valor_total: number | string | null;
  empresas_vencedoras: number;
  orgaos_compradores: number;
  valor_medio: number | string | null;
}

export interface ContratoLinha {
  numero_controle_pncp: string;
  objeto_contrato: string | null;
  orgao_razao_social: string | null;
  orgao_cnpj: string | null;
  unidade_uf: string | null;
  unidade_municipio: string | null;
  fornecedor_nome: string | null;
  fornecedor_ni: string | null;
  valor_global: number | string | null;
  data_assinatura: string | null;
}

function qs(params: Record<string, string | number | undefined>) {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '' && v !== null)
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export function useRenovacoes(
  filtros: { uf?: string; termos?: string; janelaDias?: number },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-renovacoes', portal ? 'portal' : 'app', filtros],
    queryFn: async () => {
      const body = await client.get<Envelope<ContratoRenovacao[]>>(`/renovacoes${qs(filtros)}`);
      return body.data;
    },
  });
}

export function useRankingOrgaos(
  filtros: { uf?: string; periodoMeses?: number; q?: string; cnpj?: string; limite?: number },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-ranking-orgaos', portal ? 'portal' : 'app', filtros],
    queryFn: async () => {
      const body = await client.get<Envelope<RankingOrgao[]>>(`/ranking-orgaos${qs(filtros)}`);
      return body.data;
    },
  });
}

/** Filtros compartilhados da aba Contratos. */
export interface ContratosFiltros {
  uf?: string;
  periodoMeses?: number;
  q?: string;
  cnpj?: string;
  limite?: number;
  [k: string]: string | number | undefined;
}

export function useResumoContratos(filtros: ContratosFiltros, portal?: boolean) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-resumo-contratos', portal ? 'portal' : 'app', filtros],
    queryFn: async () => {
      const body = await client.get<Envelope<ResumoContratos>>(`/resumo${qs(filtros)}`);
      return body.data;
    },
  });
}

export function useContratosLista(filtros: ContratosFiltros, portal?: boolean) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-contratos-lista', portal ? 'portal' : 'app', filtros],
    queryFn: async () => {
      const body = await client.get<Envelope<ContratoLinha[]>>(`/lista${qs(filtros)}`);
      return body.data;
    },
  });
}

export function useRankingFornecedores(
  filtros: { uf?: string; periodoMeses?: number; q?: string; cnpj?: string; limite?: number },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-ranking-fornecedores', portal ? 'portal' : 'app', filtros],
    queryFn: async () => {
      const body = await client.get<Envelope<RankingFornecedor[]>>(`/ranking-fornecedores${qs(filtros)}`);
      return body.data;
    },
  });
}

export function useGrandesContratos(
  filtros: { uf?: string; minValor?: number },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-grandes', portal ? 'portal' : 'app', filtros],
    queryFn: async () => {
      const body = await client.get<Envelope<GrandeContrato[]>>(`/grandes${qs(filtros)}`);
      return body.data;
    },
  });
}

export interface ItemMaisComprado {
  chave: string;
  descricao: string | null;
  catalogo_codigo: string | null;
  material_servico: string | null;
  editais: number;
  quantidade_total: number | string | null;
  valor_total: number | string | null;
}

export interface RaioXLinha {
  uf: string | null;
  editais: number;
  quantidade_total: number | string | null;
  valor_total: number | string | null;
  valor_unitario_medio: number | string | null;
}

export interface RaioXOnde {
  uf: string | null;
  municipio: string | null;
  editais: number;
  valor_total: number | string | null;
}

export interface RaioXComo {
  modalidade: string | null;
  editais: number;
  valor_total: number | string | null;
}

export interface RaioXEvolucao {
  mes: string;
  editais: number;
  valor_total: number | string | null;
}

export interface RaioXFaixa {
  faixa: number;
  label: string;
  registros: number;
  valor_total: number | string | null;
}

export interface RaioXQuem {
  orgao: string | null;
  orgao_cnpj: string | null;
  uf: string | null;
  editais: number;
  valor_total: number | string | null;
  numero_controle_pncp: string | null;
}

export interface RaioXEnvelope {
  quanto: {
    editais: number;
    quantidade_total: number | string | null;
    valor_total: number | string | null;
    valor_unitario_medio: number | string | null;
    valor_min: number | string | null;
    valor_max: number | string | null;
    valor_mediana: number | string | null;
    registros_com_valor: number;
  } | null;
  onde: RaioXOnde[];
  como: RaioXComo[];
  por_uf: RaioXLinha[];
  evolucao: RaioXEvolucao[];
  faixas: RaioXFaixa[];
  quem: RaioXQuem[];
}

/** Filtros da aba Raio-X (todos opcionais exceto termo). */
export interface RaioXFiltros {
  termo: string;
  uf?: string;
  ufs?: string;
  municipio?: string;
  periodoMeses?: number;
  modalidade?: string;
  orgao?: string;
  valorMin?: number;
  valorMax?: number;
  [k: string]: string | number | undefined;
}

export interface FichaMercado {
  cnpj: string;
  nome: string | null;
  total_contratos: number;
  valor_total: number | string | null;
  onde: Array<{
    uf: string | null;
    municipio: string | null;
    total_contratos: number;
    valor_total: number | string | null;
  }>;
  contratos: Array<{
    numero_controle_pncp: string;
    objeto_contrato: string | null;
    fornecedor_nome?: string | null;
    fornecedor_ni?: string | null;
    orgao_razao_social?: string | null;
    orgao_cnpj?: string | null;
    unidade_uf: string | null;
    unidade_municipio: string | null;
    valor_global: number | string | null;
    data_assinatura: string | null;
    data_vigencia_fim: string | null;
  }>;
}

export interface WatchlistItem {
  id: number;
  escopo: string;
  tipo: 'orgao' | 'fornecedor';
  cnpj: string;
  rotulo: string | null;
  notas: string | null;
  created_at: string;
}

export function useMaisComprados(
  filtros: { uf?: string; municipio?: string; periodoMeses?: number },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-mais-comprados', portal ? 'portal' : 'app', filtros],
    queryFn: async () => {
      const body = await client.get<Envelope<ItemMaisComprado[]>>(`/raiox/mais-comprados${qs(filtros)}`);
      return body.data;
    },
  });
}

export function useRaioXProduto(
  filtros: RaioXFiltros,
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-raiox-produto', portal ? 'portal' : 'app', filtros],
    enabled: Boolean(filtros.termo && filtros.termo.trim()),
    queryFn: async () => {
      const body = await client.get<Envelope<RaioXEnvelope>>(`/raiox/produto${qs(filtros)}`);
      return body.data;
    },
  });
}

export function useFichaOrgao(
  filtros: { cnpj: string; periodoMeses?: number },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-ficha-orgao', portal ? 'portal' : 'app', filtros],
    enabled: Boolean(filtros.cnpj && filtros.cnpj.replace(/\D/g, '').length >= 11),
    queryFn: async () => {
      const body = await client.get<Envelope<FichaMercado>>(
        `/orgaos/${encodeURIComponent(filtros.cnpj)}${qs({ periodoMeses: filtros.periodoMeses })}`
      );
      return body.data;
    },
  });
}

export function useFichaFornecedor(
  filtros: { ni: string; periodoMeses?: number },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-ficha-fornecedor', portal ? 'portal' : 'app', filtros],
    enabled: Boolean(filtros.ni && filtros.ni.replace(/\D/g, '').length >= 11),
    queryFn: async () => {
      const body = await client.get<Envelope<FichaMercado>>(
        `/fornecedores/${encodeURIComponent(filtros.ni)}${qs({ periodoMeses: filtros.periodoMeses })}`
      );
      return body.data;
    },
  });
}

export function useWatchlist(tipo: 'orgao' | 'fornecedor' | undefined, portal?: boolean) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-watchlist', portal ? 'portal' : 'app', tipo],
    queryFn: async () => {
      const body = await client.get<Envelope<WatchlistItem[]>>(`/watchlist${qs({ tipo })}`);
      return body.data;
    },
  });
}

export async function adicionarWatchlist(
  body: { tipo: 'orgao' | 'fornecedor'; cnpj: string; rotulo?: string; notas?: string },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  const res = await client.post<Envelope<WatchlistItem>>('/watchlist', body);
  return res.data;
}

export async function removerWatchlist(id: number, portal?: boolean) {
  const client = mercadoClient(portal);
  await client.delete(`/watchlist/${id}`);
}

export interface AtaEsgotando {
  numero_controle_pncp_ata: string;
  numero_ata_registro_preco: string | null;
  numero_controle_pncp_compra: string | null;
  objeto_contratacao: string | null;
  nome_orgao: string | null;
  cnpj_orgao: string | null;
  nome_unidade_orgao: string | null;
  possibilidade_adesao: boolean | null;
  vigencia_inicio: string | null;
  vigencia_fim: string | null;
  dias_para_fim: number | null;
}

export function useAtasEsgotando(
  filtros: { termos?: string; janelaDias?: number },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-atas-esgotando', portal ? 'portal' : 'app', filtros],
    queryFn: async () => {
      const body = await client.get<Envelope<AtaEsgotando[]>>(`/atas/esgotando${qs(filtros)}`);
      return body.data;
    },
  });
}

// ------------------------------------------------------------------ Consultar Preços

export interface PrecosStats {
  amostras: number;
  editais: number;
  quantidade_total: number | string | null;
  valor_min: number | string | null;
  valor_mediana: number | string | null;
  valor_medio: number | string | null;
  valor_max: number | string | null;
}

export interface PrecoRegistro {
  descricao: string | null;
  catalogo_codigo: string | null;
  material_servico: string | null;
  quantidade: number | string | null;
  valor_unitario_estimado: number | string | null;
  valor_total_estimado: number | string | null;
  orgao: string | null;
  orgao_cnpj: string | null;
  uf: string | null;
  municipio: string | null;
  modalidade: string | null;
  numero_controle_pncp: string | null;
  data: string | null;
}

export interface PrecosEnvelope {
  stats: PrecosStats;
  registros: PrecoRegistro[];
}

/** Filtros da aba Consultar Preços (termo obrigatório). */
export interface PrecosFiltros {
  termo: string;
  uf?: string;
  periodoMeses?: number;
  limite?: number;
  [k: string]: string | number | undefined;
}

export function useConsultarPrecos(filtros: PrecosFiltros, portal?: boolean) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-precos', portal ? 'portal' : 'app', filtros],
    enabled: Boolean(filtros.termo && filtros.termo.trim()),
    queryFn: async () => {
      const body = await client.get<Envelope<PrecosEnvelope>>(`/precos${qs(filtros)}`);
      return body.data;
    },
  });
}

/** Benchmark federal (Compras.gov) por código CATMAT. */
export interface BenchmarkCatmat {
  codigo: string | null;
  tipo: string;
  indisponivel: boolean;
  origem_recusou: boolean;
  motivo?: string;
  descricao?: string | null;
  amostras?: number;
  minimo?: number | null;
  mediana?: number | null;
  media?: number | null;
  maximo?: number | null;
  periodo_inicio?: string | null;
  periodo_fim?: string | null;
}

export function useBenchmarkCatmat(
  filtros: { codigo: string; tipo?: 'material' | 'servico' },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-benchmark-catmat', portal ? 'portal' : 'app', filtros],
    enabled: Boolean(filtros.codigo && String(filtros.codigo).trim()),
    queryFn: async () => {
      const body = await client.get<Envelope<BenchmarkCatmat>>(`/precos/benchmark${qs(filtros)}`);
      return body.data;
    },
  });
}

// ------------------------------------------------------------------ CAPAG municípios

export type CapagNota = 'A+' | 'A' | 'B+' | 'B' | 'C' | 'D';

export interface CapagResumo {
  avaliados: number;
  boa: number;
  fraca: number;
  distribuicao: Record<CapagNota, number>;
  predominante: CapagNota | null;
}

export interface CapagUf {
  uf: string;
  avaliados: number;
  boa: number;
  fraca: number;
}

export interface CapagMunicipio {
  codigo_ibge: string;
  nome: string | null;
  uf: string | null;
  nota: CapagNota | null;
  ano_referencia: number | null;
}

export function useCapagResumo(filtros: { uf?: string }, portal?: boolean) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-capag-resumo', portal ? 'portal' : 'app', filtros],
    queryFn: async () => {
      const body = await client.get<Envelope<CapagResumo>>(`/capag/resumo${qs(filtros)}`);
      return body.data;
    },
  });
}

export function useCapagPorUf(portal?: boolean) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-capag-por-uf', portal ? 'portal' : 'app'],
    queryFn: async () => {
      const body = await client.get<Envelope<CapagUf[]>>('/capag/por-uf');
      return body.data;
    },
  });
}

export function useCapagMunicipios(
  filtros: { q: string; uf?: string; limite?: number },
  portal?: boolean
) {
  const client = mercadoClient(portal);
  return useQuery({
    queryKey: ['mercado-capag-municipios', portal ? 'portal' : 'app', filtros],
    enabled: Boolean(filtros.q && filtros.q.trim()),
    queryFn: async () => {
      const body = await client.get<Envelope<CapagMunicipio[]>>(`/capag/municipios${qs(filtros)}`);
      return body.data;
    },
  });
}
